import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import { AuthProvider, ThemeModeProvider, useAuth, useThemeMode } from "@vortx/shared-ui";
import { auth, authReady } from "./config/firebase";
import { API_BASE_URL } from "./services/api";
import { BootProvider, useBootContext } from '@vortx/shared-ui';
import RetroBoot from './components/retro/RetroBoot';
import { ActivityProvider } from "./contexts/ActivityContext";
import { CssBaseline, AppBar, Toolbar, Button, Box, IconButton, Tooltip, Typography } from "@mui/material";
import { LightMode, AccountCircle, Home, Terminal, ChevronRight } from "@mui/icons-material";
import { vortxColors, vortxTypography } from "@vortx/shared-ui";
import './styles/retro-mode.css';
import { RetroProvider, useRetroContextSafe } from "@vortx/shared-ui";
import RetroLayout from "./components/retro/RetroLayout";
import LightLayout from "./components/light/LightLayout";
import { SiteDNAApp } from '@vortx/site-dna-web';
import { FluxEVApp } from '@vortx/flux-ev-web';
import { FluxPhdApp } from '@vortx/flux-phd-web';
import { FluxEMSApp } from '@vortx/flux-ems-web';
import { ColdStorageApp } from '@vortx/flux-cold-storage-web';
import { FluxDCApp } from '@vortx/flux-dc-web';
import { PartnerPortalApp } from '@vortx/partner-portal-web';

import Login from "./pages/Login";
import JoinPage from "./pages/JoinPage";
import Dashboard from "./pages/Dashboard";
import ProjectsPage from "./pages/ProjectsPage";
import Flux from "./pages/Flux";
import Profile from "./pages/Profile";
import { NoAccess } from "@vortx/shared-ui";
import PrivateRoute, { ModuleRoute } from "./components/PrivateRoute";
import TeamSeatsPage from "./pages/admin/TeamSeatsPage";
import CompaniesPage from "./pages/admin/CompaniesPage";
import UsersPage from "./pages/admin/UsersPage";
import SubscriptionsPage from "./pages/admin/SubscriptionsPage";
import SettingsPage from "./pages/admin/SettingsPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import BillingPage from "./pages/admin/BillingPage";
import TariffManagementPage from "./pages/admin/TariffManagementPage";
import EquipmentLibraryPage from "./pages/admin/EquipmentLibraryPage";
import SolverMonitorPage from "./pages/admin/SolverMonitorPage";
import CapExEditorPage from "./pages/admin/CapExEditorPage";
import DerChargesPage from "./pages/admin/DerChargesPage";
import ExtractionReviewPage from "./pages/admin/ExtractionReviewPage";
import BugReportsPage from "./pages/admin/BugReportsPage";
import TariffApprovalPage from "./pages/admin/TariffApprovalPage";
import LoadProfilesPage from "./pages/admin/LoadProfilesPage";
import BudgetTemplatesPage from "./pages/admin/BudgetTemplatesPage";
import ActivityMonitorPage from "./pages/admin/ActivityMonitorPage";
import EVVehicleDatabasePage from "./pages/admin/EVVehicleDatabasePage";
import BugReportButton from "./components/BugReportButton";
import CompleteProfilePage from "./pages/CompleteProfilePage";
import SharedPage from "./pages/SharedPage";
import GlobalBackgroundTasks from "./components/GlobalBackgroundTasks";
import ProjectOverviewPage from "./pages/ProjectOverviewPage";
import InvestorDemo from "./pages/InvestorDemo";
import DemoModeToggle from "./components/DemoModeToggle";

// Breadcrumb component for navigation (supports both retro and light modes)
function Breadcrumb({ location, navigate, currentProject, currentScenario, isRetro, activeProject, clearProjectScope }) {
    const path = location.pathname;
    const crumbs = [];

    // Always start with Dashboard/Mission Control
    crumbs.push({
        label: isRetro ? 'Mission Control' : 'Dashboard',
        path: '/dashboard',
        icon: <Home sx={{ fontSize: 14, mr: 0.5 }} />,
    });

    // When in project scope, insert Projects > ProjectName crumbs
    if (activeProject) {
        crumbs.push({
            label: 'Projects',
            path: '/all-projects',
            onClick: () => { clearProjectScope?.(); navigate('/all-projects'); },
        });
        crumbs.push({
            label: activeProject.name,
            path: null, // Current scope — not clickable
        });
    }

    // Build breadcrumb based on current path
    if (path.startsWith('/all-projects')) {
        // Don't duplicate if activeProject already added "Projects"
        if (!activeProject) {
            crumbs.push({ label: 'Projects', path: '/all-projects' });
        }
    } else if (path.startsWith('/projects')) {
        crumbs.push({ label: 'Site DNA', path: '/projects' });

        // Check for project ID
        const projectMatch = path.match(/\/projects\/([^/]+)/);
        if (projectMatch && projectMatch[1] !== 'new') {
            const projectName = currentProject?.name || 'Project';
            crumbs.push({
                label: projectName,
                path: `/projects/${projectMatch[1]}`
            });

            // Check for scenario
            const scenarioMatch = path.match(/\/scenarios\/([^/]+)/);
            if (scenarioMatch) {
                if (scenarioMatch[1] === 'new') {
                    crumbs.push({ label: 'New Scenario', path: path });
                } else {
                    const scenarioName = currentScenario?.name || 'Scenario';
                    crumbs.push({ label: scenarioName, path: path });
                }
            }
        } else if (projectMatch && projectMatch[1] === 'new') {
            crumbs.push({ label: 'New Project', path: path });
        }
    } else if (path.startsWith('/ev')) {
        crumbs.push({ label: 'Flux EV', path: '/ev' });
        const projectMatch = path.match(/\/ev\/([^/]+)/);
        if (projectMatch) {
            crumbs.push({ label: 'Project', path: path });
        }
    } else if (path.startsWith('/cold-storage')) {
        crumbs.push({ label: 'Cold Storage', path: '/cold-storage' });
    } else if (path.startsWith('/dc')) {
        crumbs.push({ label: 'Flux DC', path: '/dc' });
        const projectMatch = path.match(/\/dc\/([^/]+)/);
        if (projectMatch) {
            crumbs.push({ label: 'Project', path: path });
        }
    } else if (path.startsWith('/partner')) {
        crumbs.push({ label: 'Partner Portal', path: '/partner' });
    } else if (path.startsWith('/ems') || path.startsWith('/flux')) {
        crumbs.push({ label: 'Flux EMS', path: '/ems' });
    } else if (path.startsWith('/phd')) {
        crumbs.push({ label: 'Flux PhD', path: '/phd' });
        const simMatch = path.match(/\/simulation\/([^/]+)/);
        if (simMatch) {
            crumbs.push({ label: 'Simulation', path: path });
        }
    } else if (path.startsWith('/admin')) {
        crumbs.push({ label: 'Admin', path: '/admin' });
        // Add specific admin page
        const adminPages = {
            '/admin/team': 'Team',
            '/admin/company': 'Company',
            '/admin/companies': 'Companies',
            '/admin/users': 'Users',
            '/admin/billing': 'Billing',
            '/admin/tariffs': 'Tariffs',
            '/admin/equipment': 'Equipment',
            '/admin/solver': 'Solver',
            '/admin/capex': 'CapEx',
            '/admin/budgets': 'Budgets',
            '/admin/activity': 'Activity',
            '/admin/ev-vehicles': 'EV Vehicles',
        };
        if (adminPages[path]) {
            crumbs.push({ label: adminPages[path], path: path });
        }
    } else if (path.startsWith('/profile')) {
        crumbs.push({ label: 'Profile', path: '/profile' });
    }

    // Don't show breadcrumb if only Dashboard
    if (crumbs.length <= 1) return null;

    // Handle breadcrumb click - allow new tab for ctrl/cmd/middle click
    const handleCrumbClick = (e, crumb, isClickable) => {
        if (!isClickable) return;
        // Allow ctrl+click, cmd+click, or middle-click to open in new tab
        if (e.ctrlKey || e.metaKey || e.button === 1) {
            return; // Let browser handle it
        }
        e.preventDefault();
        if (crumb.onClick) {
            crumb.onClick();
        } else {
            navigate(crumb.path);
        }
    };

    // Retro mode styling
    if (isRetro) {
        return (
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                background: 'rgba(0, 0, 0, 0.3)',
                padding: '0.35rem 1rem',
                borderRadius: '2px',
                border: '1px solid rgba(178, 140, 80, 0.3)',
            }}>
                {crumbs.map((crumb, index) => {
                    const isClickable = index < crumbs.length - 1 && (crumb.path || crumb.onClick);
                    return (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                            {index > 0 && (
                                <ChevronRight sx={{ fontSize: 14, color: 'rgba(255,255,255,0.3)', mx: 0.5 }} />
                            )}
                            <Box
                                component={isClickable ? 'a' : 'span'}
                                href={isClickable ? crumb.path : undefined}
                                onClick={(e) => handleCrumbClick(e, crumb, isClickable)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: isClickable ? 'pointer' : 'default',
                                    color: isClickable ? 'rgba(255,255,255,0.6)' : vortxColors.gold,
                                    textDecoration: 'none',
                                    transition: 'color 0.15s ease',
                                    '&:hover': isClickable ? {
                                        color: vortxColors.teal,
                                    } : {},
                                }}
                            >
                                {crumb.icon}
                                <Typography sx={{
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.7rem',
                                    fontWeight: index === crumbs.length - 1 ? 600 : 400,
                                }}>
                                    {crumb.label}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        );
    }

    // Light mode styling
    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            background: 'rgba(255, 255, 255, 0.15)',
            padding: '0.35rem 1rem',
            borderRadius: '6px',
        }}>
            {crumbs.map((crumb, index) => {
                const isClickable = index < crumbs.length - 1 && (crumb.path || crumb.onClick);
                return (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                        {index > 0 && (
                            <ChevronRight sx={{ fontSize: 14, color: 'rgba(255,255,255,0.4)', mx: 0.5 }} />
                        )}
                        <Box
                            component={isClickable ? 'a' : 'span'}
                            href={isClickable ? crumb.path : undefined}
                            onClick={(e) => handleCrumbClick(e, crumb, isClickable)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: isClickable ? 'pointer' : 'default',
                                color: isClickable ? 'rgba(255,255,255,0.7)' : vortxColors.white,
                                textDecoration: 'none',
                                transition: 'color 0.15s ease',
                                '&:hover': isClickable ? {
                                    color: vortxColors.white,
                                } : {},
                            }}
                        >
                            {crumb.icon}
                            <Typography sx={{
                                fontFamily: vortxTypography.body,
                                fontSize: '0.75rem',
                                fontWeight: index === crumbs.length - 1 ? 600 : 400,
                            }}>
                                {crumb.label}
                            </Typography>
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}

function NavBarWrapper() {
    const { currentUser, logout } = useAuth();
    const { isDark, toggleMode, isRetro } = useThemeMode();
    const { currentProject, currentScenario, activeProject, clearProjectScope } = useRetroContextSafe();
    const { panelPower } = useBootContext();
    const navigate = useNavigate();
    const location = useLocation();
    const headerPower = panelPower.header;

    // Apply retro-mode class to body
    useEffect(() => {
        if (isRetro) {
            document.body.classList.add('retro-mode');
        } else {
            document.body.classList.remove('retro-mode');
        }
        return () => document.body.classList.remove('retro-mode');
    }, [isRetro]);

    // Get current tool name based on route
    const getToolName = () => {
        if (location.pathname.startsWith('/all-projects')) return 'Projects';
        if (location.pathname.startsWith('/projects')) return 'Site DNA';
        if (location.pathname.startsWith('/ev')) return 'Flux EV';
        if (location.pathname.startsWith('/flux') || location.pathname.startsWith('/ems')) return 'Flux EMS';
        if (location.pathname.startsWith('/phd')) return 'Flux PhD';
        if (location.pathname.startsWith('/dc')) return 'Flux DC';
        if (location.pathname.startsWith('/partner')) return 'Partner Portal';
        if (location.pathname.startsWith('/admin')) return 'Admin';
        return null;
    };
    const toolName = getToolName();

    // Check if this is a shared page (minimal navbar - no user controls)
    const isSharedPage = location.pathname.startsWith('/share');

    // Investor demo is full-screen — hide navbar entirely
    if (location.pathname.startsWith('/investor')) return null;

    return (
        <AppBar
            position="fixed"
            sx={{
                bgcolor: isDark ? '#242424' : vortxColors.gold,
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                borderBottom: isDark ? `1px solid ${vortxColors.gold}` : 'none',
                zIndex: 1400,  // Above MUI Dialog (1300) so header is always accessible
                ...(headerPower < 1 ? {
                    opacity: headerPower > 0 ? Math.min(headerPower * 1.5, 1) : 0,
                    boxShadow: headerPower > 0.3
                        ? `inset 0 0 ${headerPower * 30}px rgba(63, 106, 100, ${headerPower * 0.1})`
                        : 'none',
                    filter: headerPower < 0.8 ? `brightness(${0.5 + headerPower * 0.6})` : undefined,
                    transition: 'none',
                } : {}),
            }}
        >
            <Toolbar>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Link
                        to="/"
                        style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
                    >
                        <img
                            src="/assets/vortx-logo.png"
                            alt="Vortx"
                            style={{ height: '32px', marginRight: '8px' }}
                        />
                    </Link>
                    {toolName && (
                        <Typography
                            variant="h6"
                            sx={{
                                fontFamily: vortxTypography.heading,
                                fontWeight: 700,
                                color: vortxColors.white,
                                borderLeft: `2px solid ${isDark ? vortxColors.gold : 'rgba(255,255,255,0.5)'}`,
                                pl: 2,
                                ml: 1
                            }}
                        >
                            {toolName}
                        </Typography>
                    )}
                </Box>

                {/* Center breadcrumb - shown in both modes, hidden on shared pages */}
                <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                    {!isSharedPage && (
                        <Breadcrumb
                            location={location}
                            navigate={navigate}
                            currentProject={currentProject}
                            currentScenario={currentScenario}
                            isRetro={isRetro}
                            activeProject={activeProject}
                            clearProjectScope={clearProjectScope}
                        />
                    )}
                </Box>

                {/* Background Tasks - shown when logged in, hidden on shared pages */}
                {currentUser && !isSharedPage && <GlobalBackgroundTasks isRetro={isRetro} />}

                {/* Demo Mode Toggle - shown for demo users */}
                {currentUser && !isSharedPage && <DemoModeToggle />}

                {/* Theme Toggle - switches between Light and Retro modes */}
                <Tooltip title={isRetro ? "Switch to Light Mode" : "Switch to Retro Mode"}>
                    <IconButton
                        onClick={toggleMode}
                        sx={{
                            color: isRetro ? vortxColors.teal : vortxColors.white,
                            mr: 1,
                            border: isRetro ? `1px solid ${vortxColors.teal}` : 'none',
                            '&:hover': {
                                bgcolor: isRetro ? 'rgba(95, 212, 196, 0.2)' : 'rgba(255, 255, 255, 0.2)'
                            }
                        }}
                    >
                        {isRetro ? <LightMode /> : <Terminal />}
                    </IconButton>
                </Tooltip>

                {/* Bug Report, Profile, Logout - hidden on shared pages */}
                {!isSharedPage && (
                    <>
                        {currentUser && <BugReportButton isDark={isDark} />}

                        {currentUser ? (
                            <>
                                <Tooltip title="Profile">
                                    <IconButton
                                        component="a"
                                        href="/profile"
                                        onClick={(e) => {
                                            if (e.ctrlKey || e.metaKey || e.button === 1) return;
                                            e.preventDefault();
                                            navigate('/profile');
                                        }}
                                        sx={{
                                            color: isDark ? vortxColors.gold : vortxColors.white,
                                            mr: 1,
                                            '&:hover': {
                                                bgcolor: isDark ? 'rgba(178, 140, 80, 0.2)' : 'rgba(255, 255, 255, 0.2)'
                                            }
                                        }}
                                    >
                                        <AccountCircle />
                                    </IconButton>
                                </Tooltip>
                                <Button
                                    color="inherit"
                                    onClick={logout}
                                    sx={{
                                        color: isDark ? vortxColors.white : vortxColors.white,
                                        fontFamily: vortxTypography.body,
                                        fontWeight: 600,
                                        '&:hover': {
                                            bgcolor: isDark ? vortxColors.gold : vortxColors.darkGreen,
                                            color: isDark ? vortxColors.charcoal : vortxColors.gold
                                        }
                                    }}
                                >
                                    Logout
                                </Button>
                            </>
                        ) : (
                            <Button
                                component="a"
                                href="/login"
                                color="inherit"
                                onClick={(e) => {
                                    if (e.ctrlKey || e.metaKey || e.button === 1) return;
                                    e.preventDefault();
                                    navigate('/login');
                                }}
                                sx={{
                                    color: isDark ? vortxColors.white : vortxColors.white,
                                    fontFamily: vortxTypography.body,
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                    '&:hover': {
                                        bgcolor: isDark ? vortxColors.gold : vortxColors.darkGreen,
                                        color: isDark ? vortxColors.charcoal : vortxColors.gold
                                    }
                                }}
                            >
                                Login
                            </Button>
                        )}
                    </>
                )}
            </Toolbar>
        </AppBar>
    );
}

// Layout wrapper that uses RetroLayout or LightLayout based on theme
function LayoutWrapper() {
    const { isRetro } = useThemeMode();

    if (isRetro) {
        return <RetroLayout />;
    }

    return <LightLayout />;
}

// RetroProvider wrapper - always active since both layouts use RetroContext for state
function AppContextProvider({ children }) {
    return <RetroProvider>{children}</RetroProvider>;
}

function BootWrapper({ children }) {
    const { isRetro } = useThemeMode();
    // Skip retro boot sequence for public share/investor pages
    const isSharePage = window.location.pathname.startsWith('/share') || window.location.pathname.startsWith('/investor');
    return (
        <BootProvider isRetro={isSharePage ? false : isRetro}>
            {children}
            {!isSharePage && <RetroBoot />}
        </BootProvider>
    );
}

function AuthReadyReporter() {
    const { reportReady, reportAuthResolved } = useBootContext();
    const { currentUser } = useAuth();

    // Signal that auth loading is complete (regardless of login state)
    useEffect(() => { reportAuthResolved(); }, [reportAuthResolved]);

    // Signal ready only when user is actually authenticated
    useEffect(() => { if (currentUser) reportReady('auth'); }, [currentUser, reportReady]);

    return null;
}

// Dynamic document title based on current route and context
function DocumentTitle() {
    const location = useLocation();
    const { currentProject, currentScenario } = useRetroContextSafe();
    const path = location.pathname;

    useEffect(() => {
        const parts = [];

        const adminPages = {
            '/admin/team': 'Team',
            '/admin/company': 'Company',
            '/admin/companies': 'Companies',
            '/admin/users': 'Users',
            '/admin/billing': 'Billing',
            '/admin/tariffs': 'Tariffs',
            '/admin/equipment': 'Equipment',
            '/admin/solver': 'Solver',
            '/admin/capex': 'CapEx',
            '/admin/budgets': 'Budgets',
            '/admin/activity': 'Activity',
            '/admin/ev-vehicles': 'EV Vehicles',
            '/admin/bugs': 'Bug Reports',
            '/admin/load-profiles': 'Load Profiles',
            '/admin/der-charges': 'DER Charges',
            '/admin/extractions': 'Extractions',
            '/admin/tariff-approval': 'Tariff Approval',
            '/admin/subscriptions': 'Subscriptions',
            '/admin/settings': 'Settings',
        };

        if (path.startsWith('/investor')) {
            parts.push('Investor Demo');
        } else if (path.startsWith('/share')) {
            parts.push('Shared Page');
        } else if (path.startsWith('/all-projects')) {
            parts.push('Projects');
        } else if (path.startsWith('/projects')) {
            if (currentScenario?.name && currentProject?.name) {
                parts.push(currentScenario.name, currentProject.name);
            } else if (currentProject?.name) {
                parts.push(currentProject.name);
            }
            parts.push('Site DNA');
        } else if (path.startsWith('/ev')) {
            parts.push('Flux EV');
        } else if (path.startsWith('/ems') || path.startsWith('/flux')) {
            parts.push('Flux EMS');
        } else if (path.startsWith('/cold-storage')) {
            parts.push('Cold Storage');
        } else if (path.startsWith('/dc')) {
            parts.push('Flux DC');
        } else if (path.startsWith('/phd')) {
            parts.push('Flux PhD');
        } else if (path.startsWith('/partner')) {
            parts.push('Partner Portal');
        } else if (path.startsWith('/admin')) {
            if (adminPages[path]) {
                parts.push(adminPages[path]);
            }
            parts.push('Admin');
        } else if (path.startsWith('/profile')) {
            parts.push('Profile');
        } else if (path === '/dashboard' || path === '/') {
            parts.push('Dashboard');
        } else if (path.startsWith('/login')) {
            parts.push('Login');
        }

        parts.push('Vortx Flux');
        document.title = parts.join(' · ');
    }, [path, currentProject?.name, currentScenario?.name]);

    return null;
}

// Main app content (wrapped by AppContextProvider)
function AppContent() {
    const location = useLocation();
    const isInvestorPage = location.pathname.startsWith('/investor');
    return (
        <>
            <AuthReadyReporter />
            <DocumentTitle />
            <NavBarWrapper />
            {!isInvestorPage && <Toolbar />} {/* Spacer for fixed AppBar — hidden on investor demo */}
            <Routes>
                {/* Public routes - no auth required */}
                <Route path="/share/:type/:shareId" element={<SharedPage />} />
                <Route path="/investor" element={<InvestorDemo />} />
                <Route path="/investor/:step" element={<InvestorDemo />} />

                <Route path="/login" element={<Login />} />
                <Route path="/join" element={<JoinPage />} />
                <Route path="/no-access" element={<NoAccess appName="Vortx Flux" />} />
                <Route path="/complete-profile" element={<PrivateRoute><CompleteProfilePage /></PrivateRoute>} />

                {/* Wrapped routes - use RetroLayout when in retro mode */}
                <Route element={<LayoutWrapper />}>
                    <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                    <Route path="/all-projects" element={<PrivateRoute><ProjectsPage /></PrivateRoute>} />
                    <Route path="/all-projects/:projectId" element={<PrivateRoute><ProjectOverviewPage /></PrivateRoute>} />
                    <Route path="/projects/*" element={<PrivateRoute><SiteDNAApp /></PrivateRoute>} />
                    <Route path="/flux" element={<ModuleRoute requiredProduct="flux_ems"><Flux /></ModuleRoute>} />
                    <Route path="/ems/*" element={<ModuleRoute requiredProduct="flux_ems"><FluxEMSApp /></ModuleRoute>} />
                    <Route path="/phd/*" element={<ModuleRoute requiredProduct="flux_phd"><FluxPhdApp /></ModuleRoute>} />
                    <Route path="/ev/*" element={<PrivateRoute><FluxEVApp /></PrivateRoute>} />
                    <Route path="/cold-storage/*" element={<PrivateRoute><ColdStorageApp /></PrivateRoute>} />
                    <Route path="/dc/*" element={<PrivateRoute><FluxDCApp /></PrivateRoute>} />
                    <Route path="/partner/*" element={<ModuleRoute requiredProduct="partner_portal"><PartnerPortalApp /></ModuleRoute>} />
                    <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
                    <Route path="/admin" element={<PrivateRoute><AdminDashboard /></PrivateRoute>} />
                    <Route path="/admin/team" element={<PrivateRoute><TeamSeatsPage /></PrivateRoute>} />
                    <Route path="/admin/company" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
                    <Route path="/admin/companies" element={<PrivateRoute><CompaniesPage /></PrivateRoute>} />
                    <Route path="/admin/users" element={<PrivateRoute><UsersPage /></PrivateRoute>} />
                    <Route path="/admin/subscriptions" element={<PrivateRoute><SubscriptionsPage /></PrivateRoute>} />
                    <Route path="/admin/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
                    <Route path="/admin/billing" element={<PrivateRoute><BillingPage /></PrivateRoute>} />
                    <Route path="/admin/tariffs" element={<PrivateRoute><TariffManagementPage /></PrivateRoute>} />
                    <Route path="/admin/equipment" element={<PrivateRoute><EquipmentLibraryPage /></PrivateRoute>} />
                    <Route path="/admin/solver" element={<PrivateRoute><SolverMonitorPage /></PrivateRoute>} />
                    <Route path="/admin/capex" element={<PrivateRoute><CapExEditorPage /></PrivateRoute>} />
                    <Route path="/admin/der-charges" element={<PrivateRoute><DerChargesPage /></PrivateRoute>} />
                    <Route path="/admin/extractions" element={<PrivateRoute><ExtractionReviewPage /></PrivateRoute>} />
                    <Route path="/admin/bugs" element={<PrivateRoute><BugReportsPage /></PrivateRoute>} />
                    <Route path="/admin/tariff-approval" element={<PrivateRoute><TariffApprovalPage /></PrivateRoute>} />
                    <Route path="/admin/load-profiles" element={<PrivateRoute><LoadProfilesPage /></PrivateRoute>} />
                    <Route path="/admin/budgets" element={<PrivateRoute><BudgetTemplatesPage /></PrivateRoute>} />
                    <Route path="/admin/activity" element={<PrivateRoute><ActivityMonitorPage /></PrivateRoute>} />
                    <Route path="/admin/ev-vehicles" element={<PrivateRoute><EVVehicleDatabasePage /></PrivateRoute>} />
                    <Route path="/" element={<Navigate to="/dashboard" />} />
                </Route>
            </Routes>
        </>
    );
}

// Suppress console output on shared pages for external users
if (window.location.pathname.startsWith('/share')) {
    const noop = () => {};
    const originalConsole = { ...console };
    // Check if current user is a Vortx team member (restore console if so)
    window.__restoreConsole = () => Object.assign(console, originalConsole);
    Object.assign(console, {
        log: noop, info: noop, warn: noop, debug: noop,
        table: noop, group: noop, groupEnd: noop, time: noop, timeEnd: noop,
        error: noop,
    });
    // Restore console for Vortx users once auth loads
    auth.onAuthStateChanged((user) => {
        if (user?.email?.endsWith('@vortx.us')) {
            window.__restoreConsole();
        }
    });
}

function App() {
    // Wait for Firebase auth persistence to be established before mounting AuthProvider.
    // Without this, onAuthStateChanged can fire with null before localStorage persistence
    // is set up, causing a forced re-login on every page refresh.
    const [persistenceReady, setPersistenceReady] = useState(false);
    useEffect(() => {
        authReady.then(() => setPersistenceReady(true));
    }, []);

    // Select all text in number inputs on focus so typing replaces instead of appends
    useEffect(() => {
        const handleFocus = (e) => {
            if (e.target.tagName === 'INPUT' && e.target.type === 'number') {
                e.target.select();
            }
        };
        document.addEventListener('focus', handleFocus, true);
        return () => document.removeEventListener('focus', handleFocus, true);
    }, []);

    if (!persistenceReady) return null;

    return (
        <ThemeModeProvider>
            <CssBaseline />
            <BootWrapper>
                <AuthProvider apiBaseUrl={API_BASE_URL} firebaseAuth={auth} profileEndpoint="/api/user/profile">
                    <BrowserRouter>
                        <ActivityProvider>
                            <AppContextProvider>
                                <AppContent />
                            </AppContextProvider>
                        </ActivityProvider>
                    </BrowserRouter>
                </AuthProvider>
            </BootWrapper>
        </ThemeModeProvider>
    );
}

export default App;
